"use client";

import type { DocumentNode } from "graphql";
import {
  informationMediaMicroStandardCardDataFragmentDoc,
  Maybe,
  informationMediaMicroStandardCard,
} from "@gql/graphql";
import { InformationMediaMicroCard } from "@nrldigitaltechnology/ui-community";
import { StandardCard } from "@nrldigitaltechnology/ui-community/dist/components/InformationMediaMicroCard/types";

type InformationMediaMicroStandardCardDataProps = {
  title?: string;
  // Accept either a single item or an array so the component can be used
  // both inside a group (array) and standalone (single object).
  data:
    | Maybe<informationMediaMicroStandardCard>
    | Maybe<Maybe<informationMediaMicroStandardCard>[]>
    | undefined;
  inEditMode?: boolean;
  hideTitle?: boolean; // optional manual override
};

interface InformationMediaMicroStandardCardProps
  extends React.FC<InformationMediaMicroStandardCardDataProps> {
  getDataFragment: () => [string, DocumentNode];
}

const InformationMediaMicroStandardCards: InformationMediaMicroStandardCardProps = ({
  title,
  data,
  inEditMode = false,
  hideTitle = false,
}: InformationMediaMicroStandardCardDataProps) => {
  // Normalise data so we always work with an array
  const items =
    Array.isArray(data) && data.length > 0
      ? data
      : data
      ? [data]
      : [];

  // Map CMS data into UI card format
  const standardCards: StandardCard[] =
    items
      ?.filter((item): item is informationMediaMicroStandardCard => !!item)
      .map((item, index) => ({
        id: index,
        overlay: item?.title || "",
        type: "standard",
        image: item?.backgroundImage?.url?.default || "",
        text: item?.text || "",
        downloadPdf: "Download PDF",
        actionUrl: item?.ctaDownloadPdf?.url?.default || "",
        cardCtaUrl: item?.cardCtaUrl?.default || "",
        onClickAction: (card) => {
          if (card?.actionUrl) {
            const link = document.createElement("a");
            link.href = card.actionUrl;
            link.download = card.overlay || "document.pdf";
            link.target = "_blank";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        },
        onClickCard: (card) => {
          if (card?.cardCtaUrl) {
            window.open(card.cardCtaUrl, "_blank", "noopener,noreferrer");
          }
        },
      })) || [];

  const hasCards = standardCards.length > 0;

  return (
    <div className="flex flex-wrap justify-start gap-4 p-4">
      {/* Only pass props that InformationMediaMicroCard expects, NOT inEditMode */}
      <InformationMediaMicroCard
        title={!hideTitle && hasCards ? title : ""}
        cards={standardCards}
      />
      {inEditMode && hasCards && (
        <div className="mt-2 text-sm text-gray-500">
          You are in edit mode. Cards are visible here for preview.
        </div>
      )}
    </div>
  );
};

InformationMediaMicroStandardCards.displayName = "Media Standard Card";

InformationMediaMicroStandardCards.getDataFragment = (): [string, DocumentNode] => [
  "InformationMediaCardDataProps",
  informationMediaMicroStandardCardDataFragmentDoc,
];

export default InformationMediaMicroStandardCards;
